"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import * as Sentry from "@sentry/nextjs";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ReviewForm from "@/components/review/ReviewForm";
import ReviewCard from "@/components/review/ReviewCard";
import { cn } from "@/lib/utils";
import { isNetworkAbortError } from "@/lib/sentry/network-abort";
import type { ProductReview, ReviewStats, ReviewEligibility } from "@/types";
import {
  getProductReviewsAction,
  getReviewStatsAction,
  checkReviewEligibilityAction,
} from "@/app/actions/reviews";

interface ProductReviewsProps {
  productId: string;
  partnerId: string;
  currentUserId?: string;
  /**
   * 보는 사람의 로그인 여부가 확정됐는지(AuthProvider status !== "loading").
   * 목록은 서버가 쿠키로 차단 목록을 적용해 주지만, 확정 전에 받으면 로그인 확인 직후
   * 한 번 더 받게 되므로 확정될 때까지 기다린다. 생략하면 즉시 받는다.
   */
  viewerResolved?: boolean;
  onAuthRequired?: () => void;
  /**
   * 서버에서 미리 받은 첫 페이지. 상품 상세(ISR)는 넘기지 않는다 — 캐시된 HTML 에
   * 보는 사람별 차단 필터를 적용할 수 없어서 마운트 후 서버 액션으로 받는다.
   */
  initialReviews?: ProductReview[];
  initialStats?: ReviewStats;
  initialTotal?: number;
}

const ITEMS_PER_PAGE = 10;

export default function ProductReviews({
  productId,
  partnerId,
  currentUserId,
  viewerResolved = true,
  onAuthRequired,
  initialReviews,
  initialStats,
  initialTotal,
}: ProductReviewsProps) {
  const t = useTranslations("reviews");
  const tCommon = useTranslations("common");
  const locale = useLocale();
  const reviewsHref = `/${locale}/products/${productId}/reviews`;

  const hasInitialReviews = !!initialReviews;
  const hasInitialStats = !!initialStats;
  const [reviews, setReviews] = useState<ProductReview[]>(initialReviews || []);
  const [stats, setStats] = useState<ReviewStats | null>(initialStats || null);
  const [eligibility, setEligibility] = useState<ReviewEligibility | null>(
    null,
  );
  const [total, setTotal] = useState(initialTotal || 0);
  const [loading, setLoading] = useState(!hasInitialReviews);
  // 서버 집계가 0건이면 첫 조회는 조용히 한다 — 스켈레톤 카드를 SSR 했다가 0건 확인 뒤
  // 통째로 사라지며 아래 콘텐츠가 위로 당겨지던 CLS(승인 상품 96%가 리뷰 0건, 감사 06 #9)를
  // 없앤다. 그 사이엔 아무것도 그리지 않고, 실제로 리뷰가 있으면(캐시가 낡은 경우) 그때 나온다.
  // 작성·수정 후 다시 받을 때는 평소처럼 스켈레톤을 보인다.
  const [quietLoad, setQuietLoad] = useState(
    !hasInitialReviews && initialTotal === 0,
  );
  const showSkeleton = loading && !quietLoad;
  // 목록 조회 실패(네트워크·서버 오류) — 빈 목록과 구분해 재시도 버튼을 보인다.
  const [loadFailed, setLoadFailed] = useState(false);

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState<ProductReview | null>(
    null,
  );

  const sliderRef = useRef<HTMLDivElement>(null);
  const scrollSlider = (direction: "left" | "right") => {
    if (!sliderRef.current) return;
    const cardStep = sliderRef.current.clientWidth / 2.5 + 12;
    sliderRef.current.scrollBy({
      left: direction === "left" ? -cardStep : cardStep,
      behavior: "smooth",
    });
  };

  const fetchReviews = useCallback(
    async (pageNum: number) => {
      try {
        setLoading(true);
        setLoadFailed(false);
        const result = await getProductReviewsAction(
          productId,
          pageNum,
          ITEMS_PER_PAGE,
        );
        if (result.error) {
          // 서버가 조회 실패를 빈 목록으로 돌려준 경우 — "리뷰 없음"으로 숨기지 않는다.
          setLoadFailed(true);
          return;
        }
        setReviews(result.reviews);
        setTotal(result.total);
      } catch (error) {
        // 서버 액션 POST 중단(페이지 전환·WebView 수명주기)도 여기로 온다.
        // 중단은 다시 시도 화면만 보여주고 Sentry 엔 보내지 않는다(WEB-G).
        console.error("Error fetching reviews:", error);
        if (!isNetworkAbortError(error)) {
          Sentry.captureException(error, {
            tags: { component: "ProductReviews", action: "getProductReviews" },
            extra: { productId },
            level: "warning",
          });
        }
        setLoadFailed(true);
      } finally {
        setLoading(false);
        setQuietLoad(false);
      }
    },
    [productId],
  );

  const fetchStatsAndEligibility = useCallback(async () => {
    const [statsResult, eligibilityResult] = await Promise.all([
      getReviewStatsAction(productId),
      currentUserId
        ? checkReviewEligibilityAction(productId)
        : Promise.resolve(null),
    ]);
    setStats(statsResult);
    setEligibility(eligibilityResult);
  }, [productId, currentUserId]);

  useEffect(() => {
    // 로그인 확인 전에는 받지 않는다 — 확정되면(그리고 사용자가 바뀌면) 다시 돈다.
    if (!viewerResolved) return;
    if (!hasInitialReviews) {
      fetchReviews(1);
    }
    if (!hasInitialStats) {
      fetchStatsAndEligibility();
    } else if (currentUserId) {
      checkReviewEligibilityAction(productId).then(setEligibility);
    }
  }, [
    fetchReviews,
    fetchStatsAndEligibility,
    hasInitialReviews,
    hasInitialStats,
    viewerResolved,
    currentUserId,
    productId,
  ]);

  // On-demand 번역: SSR에서 누락된 번역을 클라이언트에서 채움.
  const translationAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    translationAttemptedRef.current = new Set();
  }, [locale]);

  useEffect(() => {
    const needsCommentTranslation = (r: ProductReview) =>
      !r.translated_comment &&
      !!r.comment &&
      !!r.source_lang &&
      r.source_lang !== locale;
    // 파트너 답변은 슬라이더에 표시되지 않지만, 상세 방문 시점에 번역을
    // 트리거해 캐시해 두면 전체보기 페이지(ISR)가 재생성 시 집어간다.
    const needsReplyTranslation = (r: ProductReview) =>
      !r.translated_reply && !!r.reply_content && r.reply_lang !== locale;

    const toTranslate = reviews.filter(
      (r) =>
        !translationAttemptedRef.current.has(r.id) &&
        (needsCommentTranslation(r) || needsReplyTranslation(r)),
    );
    if (toTranslate.length === 0) return;

    toTranslate.forEach((r) => translationAttemptedRef.current.add(r.id));

    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        toTranslate.map(async (r) => {
          const response = await fetch("/api/translate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              entity_type: "product_review",
              entity_id: r.id,
              source_lang: r.source_lang || "ko",
              target_lang: locale,
            }),
          });
          if (!response.ok) return null;
          const result = await response.json();
          const comment = (result?.data?.comment as string | undefined) ?? null;
          const reply =
            (result?.data?.reply_content as string | undefined) ?? null;
          return comment || reply ? { id: r.id, comment, reply } : null;
        }),
      );
      if (cancelled) return;

      const successful = results
        .map((r) => (r.status === "fulfilled" ? r.value : null))
        .filter(
          (
            x,
          ): x is { id: string; comment: string | null; reply: string | null } =>
            !!x,
        );
      if (successful.length === 0) return;

      setReviews((prev) =>
        prev.map((r) => {
          const hit = successful.find((s) => s.id === r.id);
          if (!hit) return r;
          return {
            ...r,
            translated_comment:
              hit.comment && needsCommentTranslation(r)
                ? hit.comment
                : r.translated_comment,
            translated_reply:
              hit.reply && needsReplyTranslation(r)
                ? hit.reply
                : r.translated_reply,
          };
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [reviews, locale]);

  const handleWriteReview = () => {
    if (!currentUserId) {
      onAuthRequired?.();
      return;
    }
    setEditingReview(null);
    setShowReviewForm(true);
  };

  const handleEditReview = (review: ProductReview) => {
    setEditingReview(review);
    setShowReviewForm(true);
  };

  const refreshAfterChange = () => {
    fetchReviews(1);
    fetchStatsAndEligibility();
  };

  const handleFormSuccess = () => {
    setShowReviewForm(false);
    setEditingReview(null);
    refreshAfterChange();
  };

  // 작성자 차단 직후 — 그 사람의 리뷰를 이 목록에서 바로 뺀다(서버 필터는 다음 조회부터).
  const handleAuthorBlocked = (blockedUserId: string) => {
    const removed = reviews.filter((r) => r.user_id === blockedUserId).length;
    if (removed === 0) return;
    setReviews((prev) => prev.filter((r) => r.user_id !== blockedUserId));
    setTotal((n) => Math.max(0, n - removed));
  };

  if (
    !showSkeleton &&
    !loadFailed &&
    reviews.length === 0 &&
    !eligibility?.canReview
  ) {
    return null;
  }

  return (
    <div className="overflow-hidden">
      <div className="border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-baseline gap-2">
            <h2 className="text-xl font-bold">{t("title")}</h2>
            {stats && stats.totalReviews > 0 && (
              <>
                <Star className="w-4 h-4 text-orange-400 fill-orange-400 self-center" />
                <span className="text-xl font-bold text-gray-900">
                  {stats.averageRating}
                </span>
                <span className="text-sm text-gray-400">
                  ({total.toLocaleString()})
                </span>
              </>
            )}
          </div>
          <Link
            href={reviewsHref}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 shrink-0"
          >
            {t("viewAll")}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {eligibility?.canReview && (
          <Button
            variant="outline"
            size="sm"
            className="mb-4 bg-gray-100 hover:bg-gray-200 border-none transition-colors"
            onClick={handleWriteReview}
          >
            {t("writeReview")}
          </Button>
        )}

        {showSkeleton && (
          <div className="overflow-x-auto scrollbar-hide -mx-5">
            <div className="flex gap-3 pb-2 px-5">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="shrink-0 w-[82%] sm:w-[calc((100%-1.5rem)/2.5)] bg-gray-50 rounded-2xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-4 w-full mb-1" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
              <div className="shrink-0 w-2" aria-hidden />
            </div>
          </div>
        )}

        {!loading && loadFailed && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p className="text-sm text-gray-500">{tCommon("error")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchReviews(1)}
            >
              {tCommon("retry")}
            </Button>
          </div>
        )}

        {!loading && !loadFailed && reviews.length > 0 && (
          <div className="relative group">
            {reviews.length > 2 && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollSlider("left")}
                className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Previous reviews"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            <div
              ref={sliderRef}
              className="overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-5 scroll-px-5"
            >
              <div className="flex gap-3 pb-2 px-5">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className={cn(
                      "shrink-0 snap-start",
                      // 1 review fills the row, 2 split it evenly (desktop),
                      // 3+ keep the peek-scroll layout. Mobile keeps the 82%
                      // swipe width from 2 reviews up.
                      reviews.length === 1
                        ? "w-full"
                        : reviews.length === 2
                          ? "w-[82%] sm:w-[calc((100%-0.75rem)/2)]"
                          : "w-[82%] sm:w-[calc((100%-1.5rem)/2.5)]",
                    )}
                  >
                    <ReviewCard
                      id={review.id}
                      rating={review.rating}
                      comment={review.comment}
                      translatedComment={review.translated_comment}
                      sourceLang={review.source_lang}
                      createdAt={review.created_at}
                      reviewerName={review.reviewer_name ?? undefined}
                      selectedTags={review.selected_tags}
                      images={review.images}
                      authorUserId={review.user_id}
                      currentUserId={currentUserId}
                      href={reviewsHref}
                      variant="compact"
                      onEdit={() => handleEditReview(review)}
                      onDeleted={refreshAfterChange}
                      onAuthorBlocked={handleAuthorBlocked}
                    />
                  </div>
                ))}
                {/* overflow-x drops the row's right padding at scroll end
                    (cards overflow the row's padding box) — a trailing
                    spacer restores the end gap: gap-3 + w-2 ≈ px-5. */}
                <div className="shrink-0 w-2" aria-hidden />
              </div>
            </div>

            {reviews.length > 2 && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollSlider("right")}
                className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Next reviews"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingReview ? t("editReview") : t("writeReview")}
            </DialogTitle>
          </DialogHeader>
          <ReviewForm
            productId={productId}
            partnerId={partnerId}
            reservationId={
              editingReview?.reservation_id ||
              eligibility?.reservation?.id ||
              ""
            }
            existingReview={editingReview}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowReviewForm(false);
              setEditingReview(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
